import { useNavigate, useRouterState } from '@tanstack/react-router';
import { useEffect, useRef, type ReactNode } from 'react';
import { SupportSessionBanner } from '@/features/support-session';
import { shellCapabilities, useAuth } from '@/shared/auth';
import { useMediaQuery } from '@/shared/hooks/useMediaQuery';
import { cn } from '@/shared/lib/cn';
import { AreaHeaderProvider } from '@/shared/navigation';
import { ObjectScopeProvider } from '@/shared/object-scope';
import { buildAreaHeader } from './areaHeaderModel';
import { AreaFallbackHeader } from './AreaFallbackHeader';
import { resolveActivePlace, visibleAreas } from './navVisibility';
import { PhoneTabBar } from './PhoneTabBar';
import { PhoneTopBar } from './PhoneTopBar';
import { ShellSidebar } from './ShellSidebar';
import { useNavCounts } from './useNavCounts';
import { useShellMetrics } from './useShellMetrics';
import { useSidebarCollapsed } from './useSidebarCollapsed';

/**
 * Gerüst (Baukasten): der Rahmen füllt das Fenster minus 12 px (Tablet 8, Handy 0),
 * Fenster und Rahmen stehen still, nur der Inhalt scrollt. Die Breite der Seitenleiste steht als
 * `--shell-nav-width` und der Abstand außen als `--shell-inset` bereit – für feste Leisten älterer
 * Seiten, die sich am Rahmen ausrichten (dazu `useShellMetrics`).
 */
export function AdminShell({ children }: { children: ReactNode }) {
  const { user, status, logout } = useAuth();
  const navigate = useNavigate();
  const location = useRouterState({ select: (state) => state.location });
  const phone = useMediaQuery('(max-width: 767px)');
  const [collapsed, toggleCollapsed] = useSidebarCollapsed();
  const capabilities = shellCapabilities(user);
  const counts = useNavCounts();
  const search = (location.search ?? {}) as Record<string, unknown>;
  const place = resolveActivePlace(user, location.pathname, search);
  const fullHeader = buildAreaHeader(user, place, location.pathname, search, counts);
  // Hat jemand nur einen Bereich (Reinigungsrollen), sind dessen Unterpunkte am Handy schon die
  // Leiste unten; derselbe Umschalter im Kopf wäre doppelt.
  const areaHeader =
    phone && visibleAreas(user).length === 1 ? { ...fullHeader, switcher: [] } : fullHeader;
  const frame = useRef<HTMLDivElement>(null);
  const main = useRef<HTMLElement>(null);
  const bottomBar = useRef<HTMLDivElement>(null);
  useShellMetrics(frame, main, bottomBar);

  // Stirbt die Sitzung mitten in der Arbeit — deaktivierter Workspace, deaktiviertes Konto,
  // abgelaufener Refresh-Token — leert der HTTP-Interceptor den Token-Store. Ohne diesen
  // Effekt bliebe der Benutzer auf einer Seite stehen, deren Anfragen alle scheitern.
  // Die Route-Guards greifen erst bei der nächsten Navigation.
  useEffect(() => {
    if (status === 'unauthenticated') {
      void navigate({ to: '/login' });
    }
  }, [status, navigate]);

  const handleLogout = async () => {
    await logout();
    void navigate({ to: '/login' });
  };

  const navWidth = phone ? '0px' : collapsed ? '80px' : '268px';
  const shell = (
    <div
      ref={frame}
      className="bg-frame text-ink box-border h-dvh p-0 [--shell-inset:0px] md:p-2 md:[--shell-inset:8px] xl:p-3 xl:[--shell-inset:12px]"
    >
      <div
        className="bg-paper md:shadow-frame xl:rounded-frame md:rounded-frame-sm flex h-full flex-col overflow-hidden"
        style={{ ['--shell-nav-width' as string]: navWidth }}
      >
        <SupportSessionBanner />
        <div className="flex min-h-0 flex-1">
          {!phone && (
            <ShellSidebar
              collapsed={collapsed}
              onToggle={toggleCollapsed}
              place={place}
              path={location.pathname}
              search={search}
              counts={counts}
              onLogout={() => void handleLogout()}
            />
          )}
          <div className="flex min-w-0 flex-1 flex-col">
            {phone && <PhoneTopBar onLogout={() => void handleLogout()} />}
            <main
              ref={main}
              id="main-content"
              className={cn(
                'animate-page-enter flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto motion-reduce:animate-none',
                'p-4 md:p-6 xl:p-8',
              )}
            >
              <AreaHeaderProvider model={areaHeader}>
                <AreaFallbackHeader />
                {children}
              </AreaHeaderProvider>
            </main>
            {phone && (
              <div ref={bottomBar} className="flex-none">
                <PhoneTabBar
                  place={place}
                  path={location.pathname}
                  search={search}
                  counts={counts}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  // Den Kontext gibt es immer: Seiten, die ein Einzelrecht öffnet, brauchen ihn auch in der
  // schlanken Reinigungsoberfläche – dort ruht er und fragt nichts ab.
  return <ObjectScopeProvider enabled={capabilities.objectScope}>{shell}</ObjectScopeProvider>;
}
